import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { buildAccommodationCancellationPolicies } from '@luxuryescapes/lib-refunds'
import { getPropertyCancellationLabelShape, getPropertyRefundableStatus, getRefundDescriptions } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import { showsPropertyCancellationTimeline } from 'lib/offer/cancellationPolicyUtils'
import PropertyCancellationPolicyView from './PropertyCancellationPolicyView'

interface Props {
  rate: App.BasePropertyRate
  propertyTimeZone: string
  shouldClarifyRefundIsHotelOnly: boolean
}

function PropertyCancellationPolicy({
  rate,
  propertyTimeZone,
  shouldClarifyRefundIsHotelOnly,
}: Props) {
  const intl = useIntl()
  const refundDescriptions = useMemo(() => getRefundDescriptions(undefined, intl), [intl])
  const refundableStatus = getPropertyRefundableStatus(rate.headlineCancellationPolicyType)
  const refundDescription = refundDescriptions[refundableStatus]

  const { description: policyDescriptions } = useMemo(() => buildAccommodationCancellationPolicies({
    fullRefundableUntil: rate.fullyRefundableUntil,
    cancellationPolicies: rate.cancellationPolicies.map(({
      startIsoDateTime,
      endIsoDateTime,
      penaltyPrice,
      isFullyNonRefundable,
      originalDescription,
    }) => ({
      // TODO(HP-1333): Update `lib-refunds` to conform to Customer Portal interface.
      amount: penaltyPrice,
      percent: 0,
      description: originalDescription,
      start: startIsoDateTime,
      end: endIsoDateTime,
      nonRefundable: isFullyNonRefundable,
    })),
  }, {
    timezone: propertyTimeZone,
    currencyCode: rate.currency,
    hideExcludesFlights: !shouldClarifyRefundIsHotelOnly,
    locale: intl.locale,
  }), [
    rate.fullyRefundableUntil,
    rate.cancellationPolicies,
    rate.currency,
    propertyTimeZone,
    shouldClarifyRefundIsHotelOnly,
    intl.locale,
  ])

  // TODO `formattedShortDateByTimezone` includes a hack for string timestamps suffixed with `'T00:00:00.000Z'`, which we want to avoid
  const headlineCancellationPolicyStart = useMemo(
    () => rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined,
    [rate.headlineCancellationPolicyStart],
  )

  const labelShape = useMemo(() => getPropertyCancellationLabelShape({
    refundable: refundableStatus,
    headlineCancellationPolicyStart,
    propertyTimezone: propertyTimeZone,
    shouldClarifyRefundIsHotelOnly,
  }), [refundableStatus, headlineCancellationPolicyStart, propertyTimeZone, shouldClarifyRefundIsHotelOnly])

  const showsTimeline = showsPropertyCancellationTimeline(rate)

  return <PropertyCancellationPolicyView
    refundableStatus={refundableStatus}
    refundDescription={refundDescription}
    labelShape={labelShape}
    policyDescriptions={policyDescriptions}
    timelineDeadline={showsTimeline && labelShape.kind === 'date' ? labelShape.formattedDate : undefined}
  />
}

export default memo(PropertyCancellationPolicy)
